import { Navigation, Pagination, Autoplay, EffectCards } from "swiper/modules";
import Swiper from "swiper";

class CustomSwiper {
    constructor(options = {}) {
        this.swiper = null;
        this.hasSlided = false;
        this.lastMousePosition = { x: null, y: null };
        this.options = {
            swiperSelector: '.swiper',
            mobileBreakpoint: 1024,
            ...options
        };

        this.mobileBreakpoint = window.matchMedia(`(max-width: ${this.options.mobileBreakpoint}px)`);

        document.addEventListener('mousemove', (e) => {
            this.lastMousePosition = { x: e.clientX, y: e.clientY };
        });

        this.init();
        this.bindEvents();
    }

    init() {
        this.initSwiper();
    }

    bindEvents() {
        this.mobileBreakpoint.addEventListener("change", () => this.initSwiper());
    }

    initSwiper() {
        if (this.swiper) {
            this.swiper.destroy(true, true);
        }

        if (this.mobileBreakpoint.matches) {
            this.initMobileSwiper();
        } else {
            this.initDesktopSwiper();
        }
    }

    initMobileSwiper() {
        this.addSwipeIndicator();
        this.initialSlideIndex = null;

        const swiperOptions = {
            modules: [EffectCards, Navigation],
            effect: "cards",
            slidesPerView: 1,
            grabCursor: true,
            loop: true,
            navigation: {
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
            },
            on: {
                init: (swiper) => {
                    this.initialSlideIndex = swiper.activeIndex;
                },
                slideChange: (swiper) => {
                    if (!this.hasSlided &&
                        this.initialSlideIndex !== null &&
                        swiper.activeIndex !== this.initialSlideIndex) {
                        this.removeSwipeIndicator();
                        this.hasSlided = true;
                    }
                },
            },
        };

        this.swiper = new Swiper(this.options.swiperSelector, swiperOptions);
    }

    initDesktopSwiper() {
        this.swiper = new Swiper(this.options.swiperSelector, {
            modules: [Navigation, Pagination, Autoplay],
            slidesPerView: 1,
            spaceBetween: 10,
            loop: true,
            speed: 1500,
            centeredSlides: true,
            autoplay: {
                delay: 0,
                pauseOnMouseEnter: true,
                disableOnInteraction: false,
            },
            keyboard: {
                enabled: true,
                onlyInViewport: false,
            },
            mousewheel: {
                invert: true,
            },
            breakpoints: {
                768: { slidesPerView: 3, spaceBetween: 40 },
                1180: { slidesPerView: 3, spaceBetween: 60 },
                1420: { slidesPerView: 5, spaceBetween: 42 },
                1920: { slidesPerView: 5, spaceBetween: 60 },
            },
            pagination: {
                el: ".swiper-pagination",
                clickable: true,
            },
            navigation: {
                nextEl: ".swiper-button-next",
                prevEl: ".swiper-button-prev",
            },
            on: {
                init: (swiper) => {
                    this.checkMouseOverAndPause(swiper);
                },
            },
        });
    }

    checkMouseOverAndPause(swiper) {
        const el = swiper.el;
        if (!el || !swiper.autoplay) return;

        const isMouseOver = () => {
            if (this.lastMousePosition.x !== null && this.lastMousePosition.y !== null) {
                const rect = el.getBoundingClientRect();
                return (
                    this.lastMousePosition.x >= rect.left &&
                    this.lastMousePosition.x <= rect.right &&
                    this.lastMousePosition.y >= rect.top &&
                    this.lastMousePosition.y <= rect.bottom
                );
            }
            return el.matches(':hover');
        };

        const pauseAndResumeOnLeave = () => {
            swiper.autoplay.stop();
            const resumeHandler = () => {
                if (!swiper.destroyed) {
                    swiper.autoplay.start();
                }
                el.removeEventListener('mouseleave', resumeHandler);
            };
            el.addEventListener('mouseleave', resumeHandler);
        };

        if (isMouseOver()) {
            pauseAndResumeOnLeave();
        }

        const onceHandler = () => {
            if (swiper.destroyed) {
                el.removeEventListener('mousemove', onceHandler);
                return;
            }
            if (isMouseOver()) {
                pauseAndResumeOnLeave();
            }
            el.removeEventListener('mousemove', onceHandler);
        };
        el.addEventListener('mousemove', onceHandler);
    }

    addSwipeIndicator() {
        const container = document.querySelector(this.options.swiperSelector);

        if (!container) {
            console.error('Swiper container not found!');
            return;
        }

        const indicator = document.createElement('div');
        indicator.classList.add('swipe-indicator');
        indicator.innerHTML = `
            <svg class="swipe-indicator-svg" width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="96" height="96.0033" rx="48" fill="black"/>
                <g clip-path="url(#clip0_2112_1431)">
                    <path d="M24 38.668H33.3333" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M29.3333 45.3353L24 38.6686L29.3333 32.002" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M72.0003 38.668H62.667" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M66.667 45.3353L72.0003 38.6686L66.667 32.002" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M73.333 32.002L78.6663 38.6686L73.333 45.3353" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M22.6663 32.002L17.333 38.6686L22.6663 45.3353" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M38.667 65.3346V40.0013C38.667 34.8546 42.8537 30.668 48.0003 30.668C53.147 30.668 57.3337 34.8546 57.3337 40.0013V65.3346" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M52 40.002C52 37.7886 50.2133 36.002 48 36.002C45.7867 36.002 44 37.7886 44 40.002V44.002H52V40.002Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </g>
                <defs>
                    <clipPath id="clip0_2112_1431">
                        <rect width="64" height="37.3333" fill="white" transform="translate(16 29.335)"/>
                    </clipPath>
                </defs>
            </svg>
            <p>Swipe Card</p>
        `;

        container.appendChild(indicator);
    }

    removeSwipeIndicator() {
        const indicator = document.querySelector('.swipe-indicator');
        if (indicator) {
            indicator.remove();
        }
    }
}
export default CustomSwiper;
